/**
 * Backorders API
 *
 * API client for backorder operations
 */

import type {
  Backorder,
  BackorderKpis,
  BackorderStatus,
  Priority,
  FulfillBackorderDto,
} from '@lognai/plugin-orders';
import type { PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildQueryString, type ListQueryParams } from './client';

const BASE_URL = '/api/orders/backorders';

/**
 * Backorder domain filter fields
 */
export interface BackorderFilter {
  status?: BackorderStatus;
  customerId?: string;
  productId?: string;
  orderId?: string;
  priority?: Priority;
  fromDate?: Date;
  toDate?: Date;
}

/**
 * Backorder list query parameters (pagination + filters)
 */
export type BackorderFilterParams = ListQueryParams<BackorderFilter>;

/**
 * Paginated backorder list response
 */
export interface PaginatedBackorderList {
  items: Backorder[];
  pagination: PaginationMetadata | null;
}

/**
 * Get all backorders with optional filters
 */
export const listBackorders = async (
  params?: BackorderFilterParams
): Promise<PaginatedBackorderList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedBackorderList>(`${BASE_URL}${query}`);
};

/**
 * Get backorders by order ID
 */
export const getBackordersByOrder = async (
  orderId: string,
  params?: ListQueryParams
): Promise<PaginatedBackorderList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedBackorderList>(
    `${BASE_URL}/by-order/${orderId}${query}`
  );
};

/**
 * Get backorders by product ID
 */
export const getBackordersByProduct = async (
  productId: string,
  params?: ListQueryParams
): Promise<PaginatedBackorderList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedBackorderList>(
    `${BASE_URL}/by-product/${productId}${query}`
  );
};

/**
 * Get a single backorder by ID
 */
export const getBackorder = async (id: string): Promise<Backorder> => {
  return apiFetch<Backorder>(`${BASE_URL}/${id}`);
};

/**
 * Fulfill a backorder
 */
export const fulfillBackorder = async (
  id: string,
  dto: FulfillBackorderDto
): Promise<Backorder> => {
  return apiFetch<Backorder>(`${BASE_URL}/${id}/fulfill`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Cancel a backorder
 */
export const cancelBackorder = async (
  id: string,
  reason: string
): Promise<Backorder> => {
  return apiFetch<Backorder>(`${BASE_URL}/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
};

/**
 * Update backorder priority
 */
export const updateBackorderPriority = async (
  id: string,
  priority: Priority,
  reason?: string
): Promise<Backorder> => {
  return apiFetch<Backorder>(`${BASE_URL}/${id}/priority`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ priority, reason }),
  });
};

/**
 * Get backorder KPIs
 */
export const getBackorderKpis = async (): Promise<BackorderKpis> => {
  return apiFetch<BackorderKpis>(`${BASE_URL}/kpis`);
};
